import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Upload, RefreshCw, FileJson, Download, Check, X, GitMerge, ChevronLeft,
  AlertTriangle, ShieldCheck, Loader2, Copy, Search, Filter, ChevronDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import {
  efAdminImportTemplate, efAdminImportBatch, efAdminListImports,
  efAdminGetImport, efAdminDecideImportItem, efAdminApproveCleanImports,
  efAdminBulkRejectImports, efAdminPublishApprovedBatch,
} from "../../lib/api";

const STATUS_COLOR = {
  pending_review: "#E0A526",
  approved: "#5DD4B3",
  merged: "#9FA8D4",
  rejected: "#757065",
};

export const AdminEpsteinImports = ({ token, userToken = null }) => {
  const [view, setView] = useState("list"); // list | new | batch
  const [batches, setBatches] = useState([]);
  const [currentBatchId, setCurrentBatchId] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await efAdminListImports(token, userToken);
      setBatches(r.batches || []);
    } catch { toast.error("Failed to load import batches"); }
    finally { setLoading(false); }
  }, [token, userToken]);
  useEffect(() => { loadList(); }, [loadList]);

  if (view === "new") {
    return <NewImport
      token={token} userToken={userToken}
      onBack={() => setView("list")}
      onCreated={(batchId) => { setCurrentBatchId(batchId); setView("batch"); loadList(); }}
    />;
  }
  if (view === "batch" && currentBatchId) {
    return <BatchReview
      token={token} userToken={userToken} batchId={currentBatchId}
      onBack={() => { setView("list"); loadList(); }}
    />;
  }

  return (
    <div data-testid="ef-imports-panel">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2 flex items-center gap-2">
            <Upload size={13} /> // RESEARCH IMPORT
          </div>
          <h2 className="font-display text-2xl text-[#E4DFD3]">Import existing research</h2>
          <p className="text-[#A39E93] mt-1 text-sm max-w-2xl">
            JSON batch import. Nothing goes public on import — items land as <span className="text-[#E0A526]">pending review</span>,
            then materialize as <span className="text-[#A39E93]">held</span> on approval, then become public only when you publish them
            through the existing source-required flow.
          </p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <button onClick={() => setView("new")} data-testid="ef-imports-new"
            className="flex items-center gap-2 bg-[#D92D20] hover:bg-[#B92318] text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
            <Upload size={12} /> New import batch
          </button>
          <button onClick={loadList} data-testid="ef-imports-refresh"
            className="flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {batches.length === 0 ? (
        <div className="border border-dashed border-[#2D2A26] p-10 text-center font-mono text-xs uppercase tracking-widest text-[#757065]"
             data-testid="ef-imports-empty">
          No import batches yet. Start one to bring your existing research in.
        </div>
      ) : (
        <div className="border border-[#2D2A26] bg-[#131313]">
          {batches.map((b) => {
            const totals = b.counts || {};
            const status = b.status_counts || {};
            return (
              <button key={b.id}
                onClick={() => { setCurrentBatchId(b.id); setView("batch"); }}
                data-testid={`ef-imports-batch-${b.id}`}
                className="w-full text-left border-b border-[#2D2A26] px-3 py-3 hover:bg-[#0a0907]">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">{b.id}</span>
                  <span className="font-mono text-[10px] text-[#757065] ml-auto">{new Date(b.created_at).toLocaleString()}</span>
                </div>
                <div className="text-xs text-[#E4DFD3] mb-1">{b.notes || <span className="text-[#757065] italic">(no batch note)</span>}</div>
                <div className="font-mono text-[10px] text-[#A39E93]">
                  imported {totals.sources || 0} sources · {totals.entities || 0} entities · {totals.relationships || 0} relationships
                </div>
                <div className="flex gap-2 mt-1 flex-wrap">
                  {Object.entries(status).map(([k, v]) => (
                    <span key={k} style={{ color: STATUS_COLOR[k] || "#A39E93", borderColor: STATUS_COLOR[k] || "#454038" }}
                          className="font-mono text-[9px] uppercase tracking-[0.25em] border px-1.5 py-0.5">
                      {k.replace(/_/g, " ")} · {v}
                    </span>
                  ))}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const NewImport = ({ token, userToken, onBack, onCreated }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [template, setTemplate] = useState(null);

  useEffect(() => {
    (async () => {
      try { setTemplate(await efAdminImportTemplate(token, userToken)); }
      catch { /* optional */ }
    })();
  }, [token, userToken]);

  const loadTemplate = () => {
    if (!template) return;
    // strip the schema_notes from the editable starter
    const { schema_notes: _schema, ...starter } = template;
    setText(JSON.stringify(starter, null, 2));
  };

  const copyTemplate = async () => {
    if (!template) return;
    await navigator.clipboard.writeText(JSON.stringify(template, null, 2));
    toast.success("Template copied to clipboard");
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const t = await f.text();
    setText(t);
  };

  const submit = async () => {
    let payload;
    try { payload = JSON.parse(text); }
    catch (err) { toast.error("Invalid JSON: " + err.message); return; }
    setBusy(true);
    try {
      const r = await efAdminImportBatch(token, payload, userToken);
      toast.success(`Batch ${r.batch_id} created — ${r.total_items} items queued for review.`);
      onCreated(r.batch_id);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (Array.isArray(detail) ? detail.map(d => d.msg).join("; ") : "Import failed"));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="ef-imports-new">
      <button onClick={onBack} className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] hover:text-[#D92D20] mb-3">
        <ChevronLeft size={11} /> Back to imports
      </button>
      <h2 className="font-display text-2xl text-[#E4DFD3] mb-3">New import batch</h2>

      <div className="grid lg:grid-cols-[1fr_360px] gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <button onClick={loadTemplate} data-testid="ef-imports-load-template"
              className="inline-flex items-center gap-1.5 border border-[#9FA8D4]/40 hover:bg-[#0c0e1a] text-[#9FA8D4] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em]">
              <FileJson size={11} /> Load template into editor
            </button>
            <button onClick={copyTemplate}
              className="inline-flex items-center gap-1.5 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em]">
              <Copy size={11} /> Copy template JSON
            </button>
            <label className="inline-flex items-center gap-1.5 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] cursor-pointer">
              <Download size={11} className="rotate-180" /> Upload .json
              <input type="file" accept="application/json,.json" onChange={onFile}
                data-testid="ef-imports-file" className="hidden" />
            </label>
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={26}
            spellCheck={false}
            placeholder='Paste JSON here, or click "Load template" above to start.'
            data-testid="ef-imports-textarea"
            className="w-full bg-[#0a0907] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-[11px] text-[#E4DFD3] placeholder:text-[#757065]"
          />
          <button onClick={submit} disabled={busy || !text.trim()}
            data-testid="ef-imports-submit"
            className="mt-3 w-full sm:w-auto flex items-center justify-center gap-2 bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.3em]">
            {busy ? <Loader2 className="animate-spin" size={12} /> : <Upload size={12} />} Submit batch for review
          </button>
        </div>

        <div className="space-y-3">
          <div className="border border-[#2D2A26] bg-[#0F0F0F] p-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-2 flex items-center gap-2">
              <ShieldCheck size={11} /> // SAFETY RULES
            </div>
            <ul className="text-xs text-[#A39E93] space-y-1.5">
              <li>· Imported items land as <span className="text-[#E0A526]">pending_review</span>. Nothing public on import.</li>
              <li>· On approval, items become <span className="text-[#9FA8D4]">held</span> (public:false) in the live graph.</li>
              <li>· Publishing each item still requires <span className="text-[#E4DFD3]">≥1 source attached</span>.</li>
              <li>· Likely duplicates are flagged for your review — never auto-merged.</li>
              <li>· Reference an existing graph entity by <code className="bg-[#0a0907] text-[#5DD4B3] px-1">@its-slug</code>.</li>
            </ul>
          </div>

          {template && (
            <div className="border border-[#2D2A26] bg-[#0F0F0F] p-3">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-2">// SCHEMA QUICK REF</div>
              <KV k="kind" vs={template.schema_notes?.kind} />
              <KV k="relationship_type" vs={template.schema_notes?.relationship_type} />
              <KV k="source_type" vs={template.schema_notes?.source_type} />
              <div className="font-mono text-[10px] text-[#757065] mt-2 leading-relaxed">
                <span className="text-[#5DD4B3]">ref_format:</span> {template.schema_notes?.ref_format}
              </div>
              <div className="font-mono text-[10px] text-[#757065] mt-2 leading-relaxed">
                <span className="text-[#5DD4B3]">category:</span> {template.schema_notes?.category}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const KV = ({ k, vs }) => (
  <div className="font-mono text-[10px] mb-1">
    <span className="text-[#5DD4B3]">{k}:</span>{" "}
    <span className="text-[#A39E93] break-words">{(vs || []).join(" · ")}</span>
  </div>
);

const BatchReview = ({ token, userToken, batchId, onBack }) => {
  const [data, setData] = useState(null);
  const [allItems, setAllItems] = useState([]);  // full-batch snapshot for dependency resolution
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending_review");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dedupeOnly, setDedupeOnly] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      // Fetch the filtered view for display AND the full unfiltered view for
      // accurate dependency-warning resolution. Without the unfiltered fetch,
      // already-approved items vanish from refIndex when the user filters to
      // pending_review and the UI falsely reports "missing in batch".
      const [filtered, full] = await Promise.all([
        efAdminGetImport(token, batchId, statusFilter === "all" ? null : statusFilter, userToken),
        statusFilter === "all"
          ? Promise.resolve(null)
          : efAdminGetImport(token, batchId, null, userToken),
      ]);
      setData(filtered);
      setAllItems((full || filtered)?.items || []);
    } catch { toast.error("Failed to load batch"); }
    finally { setLoading(false); }
  }, [token, userToken, batchId, statusFilter]);
  useEffect(() => { load(); }, [load]);

  const decide = async (item, decision, target_id = null) => {
    setBusy(true);
    try {
      const r = await efAdminDecideImportItem(token, batchId, item.id,
        { decision, target_id }, userToken);
      toast.success(`${decision} → ${r.status}${r.new_id ? " · " + r.new_id : ""}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Decision failed"); }
    finally { setBusy(false); }
  };

  const approveClean = async () => {
    if (!window.confirm("Bulk-approve all items that have no dedupe candidates? Items with possible duplicates will be skipped for manual review.")) return;
    setBusy(true);
    try {
      const r = await efAdminApproveCleanImports(token, batchId, userToken);
      toast.success(`Approved: ${r.approved.source} sources, ${r.approved.entity} entities, ${r.approved.relationship} relationships. Skipped (dedupe): ${r.skipped_dedupe}.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk approve failed"); }
    finally { setBusy(false); }
  };

  const bulkReject = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Reject ${selected.size} selected item(s)? Only pending items will be affected.`)) return;
    setBusy(true);
    try {
      const r = await efAdminBulkRejectImports(token, batchId, [...selected], null, userToken);
      toast.success(`Rejected ${r.rejected}/${r.requested} item(s).`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk reject failed"); }
    finally { setBusy(false); }
  };

  const publishApproved = async () => {
    if (!window.confirm("Publish all approved items in this batch to the public site? Items with no source reference are skipped (anchor entities are exempt).")) return;
    setBusy(true);
    try {
      const r = await efAdminPublishApprovedBatch(token, batchId, userToken);
      const pub = r.published || {};
      const total = Object.values(pub).reduce((a, b) => a + b, 0);
      const skipped = Object.values(r.skipped_missing_source || {}).reduce((a, b) => a + b, 0);
      toast.success(`Published ${total} record(s) — sources:${pub.source||0} entities:${pub.entity||0} relationships:${pub.relationship||0} evidence:${pub.evidence||0} timeline:${pub.timeline||0}${skipped ? ` · skipped ${skipped} (missing source)` : ""}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk publish failed"); }
    finally { setBusy(false); }
  };

  // Refs registry — used for dependency warnings & "show linked" expansion.
  // Built from the FULL unfiltered batch snapshot, not the currently-filtered view,
  // so already-approved entities/sources don't show as "missing in batch" when
  // the admin filters down to pending items.
  const refIndex = useMemo(() => {
    const m = new Map();
    allItems.forEach((i) => m.set(`${i.type}:${i.ref}`, i));
    return m;
  }, [allItems]);

  const toggleSel = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  if (loading || !data) return <div className="font-mono text-xs text-[#757065]">// LOADING BATCH…</div>;
  const { batch, items } = data;

  // Apply search + type + dedupe filters
  const ql = q.trim().toLowerCase();
  const matchesQuery = (it) => {
    if (!ql) return true;
    const p = it.payload || {};
    const bag = [
      it.ref, p.title, p.name, p.url, p.source_url, p.attachment_url,
      p.publisher, p.publisher_or_origin, p.summary, p.short_summary,
      p.from, p.to, p.location_label,
    ].filter(Boolean).join(" ").toLowerCase();
    return bag.includes(ql);
  };
  const filteredItems = items
    .filter((i) => typeFilter === "all" || i.type === typeFilter)
    .filter((i) => !dedupeOnly || (i.dedupe_candidates && i.dedupe_candidates.length > 0))
    .filter(matchesQuery);

  const groups = {
    source: filteredItems.filter((i) => i.type === "source"),
    entity: filteredItems.filter((i) => i.type === "entity"),
    evidence: filteredItems.filter((i) => i.type === "evidence"),
    relationship: filteredItems.filter((i) => i.type === "relationship"),
    timeline: filteredItems.filter((i) => i.type === "timeline"),
  };

  // Type counts across the unfiltered set (so the chips don't dance when typing)
  const typeCounts = items.reduce((acc, i) => { acc[i.type] = (acc[i.type] || 0) + 1; return acc; }, {});
  const dedupeFlaggedCount = items.filter((i) => i.dedupe_candidates && i.dedupe_candidates.length > 0).length;

  const pendingVisible = filteredItems.filter((i) => i.status === "pending_review");

  return (
    <div data-testid="ef-imports-batch-view">
      <button onClick={onBack} className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] hover:text-[#D92D20] mb-3">
        <ChevronLeft size={11} /> Back to imports
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">// {batch.id}</div>
          <h2 className="font-display text-2xl text-[#E4DFD3]">Import batch review</h2>
          <p className="text-xs text-[#A39E93] mt-1">{batch.notes || <em className="text-[#757065]">(no batch note)</em>}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={bulkReject} disabled={busy || selected.size === 0} data-testid="ef-imports-bulk-reject"
            className="flex items-center gap-2 border border-[#D92D20]/60 hover:bg-[#1a0808] disabled:opacity-30 text-[#D92D20] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
            <X size={12} /> Reject selected · {selected.size}
          </button>
          <button onClick={approveClean} disabled={busy} data-testid="ef-imports-approve-clean"
            className="flex items-center gap-2 bg-[#5DD4B3] hover:bg-[#4ABF9F] disabled:opacity-40 text-[#0a0907] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
            <Check size={12} /> Approve clean items
          </button>
        </div>
      </div>

      {/* Status filter */}
      <div className="flex overflow-x-auto gap-1 mb-3 border-b border-[#2D2A26]">
        {["pending_review", "approved", "merged", "rejected", "all"].map((f) => {
          const active = statusFilter === f;
          return (
            <button key={f} onClick={() => setStatusFilter(f)} data-testid={`ef-imports-filter-${f}`}
              className={"px-3 py-2 -mb-px font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 shrink-0 " +
                (active ? "border-[#D92D20] text-[#D92D20]" : "border-transparent text-[#A39E93] hover:text-[#E4DFD3]")}>
              {f.replace(/_/g, " ")}
            </button>
          );
        })}
      </div>

      {/* Type filter + search + dedupe toggle */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex flex-wrap gap-1" data-testid="ef-imports-type-filter">
          {[
            ["all", "All"],
            ["source", "Sources"],
            ["entity", "Entities"],
            ["evidence", "Evidence"],
            ["relationship", "Relationships"],
            ["timeline", "Timeline"],
          ].map(([k, l]) => {
            const active = typeFilter === k;
            const cnt = k === "all" ? items.length : (typeCounts[k] || 0);
            return (
              <button key={k} onClick={() => setTypeFilter(k)}
                data-testid={`ef-imports-typef-${k}`}
                className={"font-mono text-[9px] uppercase tracking-[0.25em] border px-2 py-1 " +
                  (active ? "border-[#D92D20] text-[#D92D20] bg-[#1a0808]" : "border-[#454038] text-[#A39E93] hover:border-[#D92D20]")}>
                {l} <span className="text-[#757065]">· {cnt}</span>
              </button>
            );
          })}
        </div>

        <button onClick={() => setDedupeOnly((v) => !v)}
          data-testid="ef-imports-dedupe-toggle"
          className={"font-mono text-[9px] uppercase tracking-[0.25em] border px-2 py-1 inline-flex items-center gap-1 " +
            (dedupeOnly ? "border-[#E0A526] text-[#E0A526] bg-[#1a1408]" : "border-[#454038] text-[#A39E93] hover:border-[#E0A526]")}>
          <AlertTriangle size={10} /> Dedupe-flagged only <span className="text-[#757065]">· {dedupeFlaggedCount}</span>
        </button>

        <div className="flex items-center border border-[#2D2A26] bg-[#0a0907] px-2 py-1 flex-1 min-w-[200px]">
          <Search size={11} className="text-[#757065]" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="search name, title, url, ref…"
            data-testid="ef-imports-search"
            className="bg-transparent outline-none w-full pl-2 font-mono text-[11px] text-[#E4DFD3] placeholder:text-[#757065]"
          />
          {q && (
            <button onClick={() => setQ("")} className="text-[#757065] hover:text-[#D92D20]"><X size={11} /></button>
          )}
        </div>
      </div>

      {/* Select-all helper for current pending-visible set */}
      {pendingVisible.length > 0 && (
        <div className="flex items-center gap-3 mb-3 text-xs text-[#A39E93]">
          <button
            data-testid="ef-imports-select-pending-visible"
            onClick={() => {
              const allIds = pendingVisible.map((i) => i.id);
              const allSelected = allIds.every((id) => selected.has(id));
              setSelected((s) => {
                const n = new Set(s);
                if (allSelected) { allIds.forEach((id) => n.delete(id)); }
                else { allIds.forEach((id) => n.add(id)); }
                return n;
              });
            }}
            className="font-mono text-[10px] uppercase tracking-[0.25em] border border-[#454038] hover:border-[#D92D20] px-2 py-1">
            {pendingVisible.every((i) => selected.has(i.id)) ? "Unselect" : "Select"} all pending in view · {pendingVisible.length}
          </button>
          {selected.size > 0 && (
            <button onClick={() => setSelected(new Set())}
              className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#757065] hover:text-[#D92D20]">
              Clear selection ({selected.size})
            </button>
          )}
        </div>
      )}

      <Section title="SOURCES" items={groups.source}
        render={(it) => <SourceCard item={it} decide={decide} busy={busy} selected={selected.has(it.id)} onToggleSel={toggleSel} refIndex={refIndex} />} />
      <Section title="ENTITIES" items={groups.entity}
        render={(it) => <EntityCard item={it} decide={decide} busy={busy} selected={selected.has(it.id)} onToggleSel={toggleSel} refIndex={refIndex} />} />
      <Section title="EVIDENCE" items={groups.evidence}
        render={(it) => <EvidenceCard item={it} decide={decide} busy={busy} selected={selected.has(it.id)} onToggleSel={toggleSel} refIndex={refIndex} />} />
      <Section title="RELATIONSHIPS" items={groups.relationship}
        render={(it) => <RelCard item={it} decide={decide} busy={busy} selected={selected.has(it.id)} onToggleSel={toggleSel} refIndex={refIndex} />} />
      <Section title="TIMELINE EVENTS" items={groups.timeline}
        render={(it) => <TimelineCard item={it} decide={decide} busy={busy} selected={selected.has(it.id)} onToggleSel={toggleSel} refIndex={refIndex} />} />

      {filteredItems.length === 0 && (
        <div className="border border-dashed border-[#2D2A26] p-8 text-center font-mono text-xs uppercase tracking-widest text-[#757065]"
             data-testid="ef-imports-no-match">
          No items match the current filters.
        </div>
      )}
    </div>
  );
};

const Section = ({ title, items, render }) => (
  <div className="mb-6">
    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-2">// {title} · {items.length}</div>
    {items.length === 0 ? (
      <div className="font-mono text-[10px] text-[#757065] italic">none in this view</div>
    ) : (
      <div className="space-y-2">{items.map((it) => <React.Fragment key={it.id}>{render(it)}</React.Fragment>)}</div>
    )}
  </div>
);

const StatusPill = ({ status }) => (
  <span style={{ color: STATUS_COLOR[status] || "#A39E93", borderColor: STATUS_COLOR[status] || "#454038" }}
        className="font-mono text-[9px] uppercase tracking-[0.25em] border px-1.5 py-0.5">
    {(status || "").replace(/_/g, " ")}
  </span>
);

const DedupeBlock = ({ candidates, item, decide, busy }) => {
  if (!candidates?.length) return null;
  return (
    <div className="border border-[#E0A526]/40 bg-[#1a1408] px-2 py-1.5 mb-2">
      <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#E0A526] mb-1 flex items-center gap-1">
        <AlertTriangle size={10} /> POSSIBLE DUPLICATE
      </div>
      {candidates.map((c) => (
        <div key={c.id} className="flex flex-wrap items-center gap-2 text-xs py-1" data-testid={`ef-imports-dedupe-${c.id}`}>
          <span className="text-[#E4DFD3]">{c.title || c.name}</span>
          <span className="font-mono text-[9px] text-[#757065]">({c.reason})</span>
          <button onClick={() => decide(item, "merge", c.id)} disabled={busy}
            data-testid={`ef-imports-merge-${item.id}-${c.id}`}
            className="ml-auto inline-flex items-center gap-1 border border-[#9FA8D4]/40 hover:bg-[#0c0e1a] text-[#9FA8D4] px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.25em]">
            <GitMerge size={9} /> Merge into this
          </button>
        </div>
      ))}
    </div>
  );
};

const ItemActions = ({ item, decide, busy }) => {
  if (item.status !== "pending_review") {
    return (
      <div className="font-mono text-[10px] text-[#757065]">
        {item.materialized_id && <>→ <span className="text-[#5DD4B3]">{item.materialized_id}</span></>}
        {item.merged_into_id && <>→ <span className="text-[#9FA8D4]">merged into {item.merged_into_id}</span></>}
      </div>
    );
  }
  return (
    <div className="flex gap-2 flex-wrap">
      <button onClick={() => decide(item, "approve")} disabled={busy} data-testid={`ef-imports-approve-${item.id}`}
        className="inline-flex items-center gap-1 bg-[#D92D20] hover:bg-[#B92318] text-white px-2.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em]">
        <Check size={10} /> Approve (held)
      </button>
      <button onClick={() => decide(item, "reject")} disabled={busy} data-testid={`ef-imports-reject-${item.id}`}
        className="inline-flex items-center gap-1 border border-[#454038] hover:border-[#D92D20] text-[#757065] hover:text-[#D92D20] px-2.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em]">
        <X size={10} /> Reject
      </button>
    </div>
  );
};

const SelCheckbox = ({ item, selected, onToggle }) => {
  if (item.status !== "pending_review") return null;
  return (
    <label className="inline-flex items-center gap-1 shrink-0 cursor-pointer"
      title="Select for bulk reject"
      data-testid={`ef-imports-sel-${item.id}`}>
      <input
        type="checkbox" checked={!!selected} onChange={() => onToggle(item.id)}
        className="w-3 h-3 accent-[#D92D20]"
      />
    </label>
  );
};

/** Resolve a ref like '@slug' or 'src_foo' against the batch index.
 *  Returns { ok, external?, reason? }.
 *  External (@slug) refs are assumed valid — the backend already validated their
 *  shape at import time; deep DB lookup would slow this UI down for big batches.
 */
function resolveRef(refIndex, ref, expectedType) {
  if (!ref) return { ok: true };
  if (typeof ref !== "string") return { ok: true };
  if (ref.startsWith("@")) return { ok: true, external: true };
  const found = refIndex.get(`${expectedType}:${ref}`);
  if (!found) return { ok: false, reason: "missing in batch" };
  if (found.status === "approved" || found.status === "merged") return { ok: true };
  return { ok: false, reason: found.status };
}

const DependencyWarnings = ({ item, refIndex }) => {
  const p = item.payload || {};
  const warnings = [];
  const push = (msg) => warnings.push(msg);

  const checkList = (refs, expectedType, label) => {
    (refs || []).forEach((ref) => {
      const r = resolveRef(refIndex, ref, expectedType);
      if (!r.ok) push(`${label} '${ref}' — ${r.reason}`);
    });
  };

  if (item.type === "relationship") {
    [["from", "entity"], ["to", "entity"]].forEach(([k, t]) => {
      const ref = p[k];
      if (!ref) return;
      const r = resolveRef(refIndex, ref, t);
      if (!r.ok) push(`endpoint ${k}='${ref}' — ${r.reason}`);
    });
    checkList(p.source_refs, "source", "source_ref");
    checkList(p.evidence_refs, "evidence", "evidence_ref");
  } else if (item.type === "entity") {
    checkList(p.source_refs, "source", "source_ref");
    checkList(p.evidence_refs, "evidence", "evidence_ref");
  } else if (item.type === "evidence") {
    checkList(p.source_refs, "source", "source_ref");
    checkList(p.linked_entity_refs, "entity", "linked_entity_ref");
  } else if (item.type === "timeline") {
    checkList(p.source_refs, "source", "source_ref");
    checkList(p.evidence_refs, "evidence", "evidence_ref");
    checkList(p.involved_entity_refs, "entity", "involved_entity_ref");
  }

  if (warnings.length === 0) return null;
  return (
    <div className="border border-[#D92D20]/40 bg-[#1a0808] px-2 py-1.5 mb-2"
      data-testid={`ef-imports-deps-${item.id}`}>
      <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#D92D20] mb-1 flex items-center gap-1">
        <AlertTriangle size={10} /> DEPENDENCY WARNINGS · {warnings.length}
      </div>
      <ul className="text-[11px] text-[#CBB892] space-y-0.5">
        {warnings.slice(0, 5).map((w, i) => <li key={i}>— {w}</li>)}
        {warnings.length > 5 && <li className="text-[#757065] italic">…+{warnings.length - 5} more</li>}
      </ul>
      <div className="text-[10px] text-[#757065] mt-1 italic">
        Approving this item before its dependencies are approved may leave references unresolved.
      </div>
    </div>
  );
};

const CardHeader = ({ item, selected, onToggleSel, badges = [] }) => (
  <div className="flex flex-wrap items-center gap-2 mb-1">
    <SelCheckbox item={item} selected={selected} onToggle={onToggleSel} />
    <StatusPill status={item.status} />
    <span className="font-mono text-[9px] text-[#757065]">ref: {item.ref}</span>
    {badges.map((b, i) => (
      <span key={i} style={b.style}
        className="font-mono text-[9px] uppercase tracking-[0.25em]">{b.label}</span>
    ))}
  </div>
);

const SourceCard = ({ item, decide, busy, selected, onToggleSel, refIndex }) => {
  const p = item.payload;
  return (
    <div className="border border-[#2D2A26] bg-[#0F0F0F] px-3 py-2" data-testid={`ef-imports-source-${item.id}`}>
      <CardHeader item={item} selected={selected} onToggleSel={onToggleSel} badges={[
        { label: p.source_type, style: { color: "#5DD4B3" } },
        ...(p.publisher ? [{ label: p.publisher, style: { color: "#757065" } }] : []),
        ...(p.source_origin ? [{ label: p.source_origin, style: { color: "#9FA8D4" } }] : []),
      ]} />
      <div className="text-sm text-[#E4DFD3]">{p.title}</div>
      <div className="font-mono text-[10px] text-[#A39E93] break-all mb-1">
        <a href={p.url} target="_blank" rel="noopener noreferrer" className="hover:text-[#D92D20]">{p.url}</a>
      </div>
      {p.excerpt && <div className="text-xs italic text-[#CBB892] mb-2 line-clamp-2">{p.excerpt}</div>}
      <DependencyWarnings item={item} refIndex={refIndex} />
      <DedupeBlock candidates={item.dedupe_candidates} item={item} decide={decide} busy={busy} />
      <ItemActions item={item} decide={decide} busy={busy} />
    </div>
  );
};

const EntityCard = ({ item, decide, busy, selected, onToggleSel, refIndex }) => {
  const p = item.payload;
  return (
    <div className="border border-[#2D2A26] bg-[#0F0F0F] px-3 py-2" data-testid={`ef-imports-entity-${item.id}`}>
      <CardHeader item={item} selected={selected} onToggleSel={onToggleSel} badges={[
        { label: p.kind, style: { color: "#CBB892" } },
        ...(p.category ? [{ label: p.category, style: { color: "#9FA8D4" } }] : []),
        ...(p.confidence_level ? [{ label: p.confidence_level, style: { color: "#E0A526" } }] : []),
      ]} />
      <div className="text-sm text-[#E4DFD3]">{p.name}</div>
      {p.summary && <div className="text-xs text-[#A39E93] mb-2 mt-1">{p.summary}</div>}
      <div className="font-mono text-[10px] text-[#757065] mb-2">
        sources: {(p.source_refs || []).length} · evidence: {(p.evidence_refs || []).length}
      </div>
      <DependencyWarnings item={item} refIndex={refIndex} />
      <DedupeBlock candidates={item.dedupe_candidates} item={item} decide={decide} busy={busy} />
      <ItemActions item={item} decide={decide} busy={busy} />
    </div>
  );
};

const EvidenceCard = ({ item, decide, busy, selected, onToggleSel, refIndex }) => {
  const p = item.payload;
  return (
    <div className="border border-[#2D2A26] bg-[#0F0F0F] px-3 py-2" data-testid={`ef-imports-evidence-${item.id}`}>
      <CardHeader item={item} selected={selected} onToggleSel={onToggleSel} badges={[
        { label: p.evidence_type, style: { color: "#5DD4B3" } },
        ...(p.date ? [{ label: p.date, style: { color: "#CBB892" } }] : []),
        ...(p.confidence_level ? [{ label: p.confidence_level, style: { color: "#E0A526" } }] : []),
      ]} />
      <div className="text-sm text-[#E4DFD3]">{p.title}</div>
      {p.short_summary && <div className="text-xs text-[#A39E93] mb-1 mt-1">{p.short_summary}</div>}
      {p.source_url && (
        <div className="font-mono text-[10px] text-[#A39E93] break-all mb-1">
          <a href={p.source_url} target="_blank" rel="noopener noreferrer" className="hover:text-[#D92D20]">{p.source_url}</a>
        </div>
      )}
      <div className="font-mono text-[10px] text-[#757065] mb-2">
        sources: {(p.source_refs || []).length} · linked entities: {(p.linked_entity_refs || []).length}
      </div>
      <DependencyWarnings item={item} refIndex={refIndex} />
      <DedupeBlock candidates={item.dedupe_candidates} item={item} decide={decide} busy={busy} />
      <ItemActions item={item} decide={decide} busy={busy} />
    </div>
  );
};

const RelCard = ({ item, decide, busy, selected, onToggleSel, refIndex }) => {
  const p = item.payload;
  return (
    <div className="border border-[#2D2A26] bg-[#0F0F0F] px-3 py-2" data-testid={`ef-imports-rel-${item.id}`}>
      <CardHeader item={item} selected={selected} onToggleSel={onToggleSel} badges={[
        { label: p.type, style: { color: "#CBB892" } },
        { label: p.confidence, style: { color: "#E0A526" } },
        ...(p.evidence_type ? [{ label: p.evidence_type, style: { color: "#5DD4B3" } }] : []),
      ]} />
      <div className="text-sm text-[#E4DFD3]">{p.from} → {p.to}</div>
      {p.summary && <div className="text-xs text-[#A39E93] mb-2 mt-1">{p.summary}</div>}
      <div className="font-mono text-[10px] text-[#757065] mb-2">
        sources: {(p.source_refs || []).length} · evidence: {(p.evidence_refs || []).length}
        {p.date_label && <span> · {p.date_label}</span>}
        {p.location_label && <span> · @ {p.location_label}</span>}
      </div>
      <DependencyWarnings item={item} refIndex={refIndex} />
      <ItemActions item={item} decide={decide} busy={busy} />
    </div>
  );
};

const TimelineCard = ({ item, decide, busy, selected, onToggleSel, refIndex }) => {
  const p = item.payload;
  return (
    <div className="border border-[#2D2A26] bg-[#0F0F0F] px-3 py-2" data-testid={`ef-imports-timeline-${item.id}`}>
      <CardHeader item={item} selected={selected} onToggleSel={onToggleSel} badges={[
        { label: p.event_type, style: { color: "#9FA8D4" } },
        ...(p.date ? [{ label: p.date, style: { color: "#CBB892" } }] : []),
        ...(p.confidence_level ? [{ label: p.confidence_level, style: { color: "#E0A526" } }] : []),
      ]} />
      <div className="text-sm text-[#E4DFD3]">{p.title}</div>
      {p.summary && <div className="text-xs text-[#A39E93] mb-1 mt-1">{p.summary}</div>}
      {p.location_label && <div className="font-mono text-[10px] text-[#757065]">@ {p.location_label}</div>}
      <div className="font-mono text-[10px] text-[#757065] mb-2">
        sources: {(p.source_refs || []).length} · evidence: {(p.evidence_refs || []).length} · entities: {(p.involved_entity_refs || []).length}
      </div>
      <DependencyWarnings item={item} refIndex={refIndex} />
      <DedupeBlock candidates={item.dedupe_candidates} item={item} decide={decide} busy={busy} />
      <ItemActions item={item} decide={decide} busy={busy} />
    </div>
  );
};

export default AdminEpsteinImports;
